import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import FestivalModal from './FestivalModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Festival {
  festival_id: number;
  festival_name: string;
  festival_status: number;
}

const Festivals: React.FC = () => {
  const [festivals, setFestivals] = useState<Festival[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Festival | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const cancel: PopconfirmProps['onCancel'] = () => {
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchFestivals = async () => {
    try {
      const data = await apiRequest<Festival[]>({
        method: 'get',
        url: 'festival',
      });
      setFestivals(data);
    } catch (error) {
      showError('Failed to fetch festivals');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchFestivals();
  }, []);

  const handleEditClick = (record: Festival) => {
    setIsEditMode(true);
    setInitialValues(record);
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchFestivals();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (festival_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-festival/${festival_id}`,
      });
      showSuccess('Festival deleted');
      fetchFestivals();
    } catch {
      showError('Failed to delete festival');
    }
  };

  const handleStatusToggle = async (festivalId: number) => {
    setFestivals((prev) =>
      prev.map((item) =>
        item.festival_id === festivalId
          ? { ...item, festival_status: item.festival_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-festival/${festivalId}`,
      });
      showSuccess('Festival status updated');
    } catch (error) {
      showError('Failed to update status');
      setFestivals((prev) =>
        prev.map((item) =>
          item.festival_id === festivalId
            ? { ...item, festival_status: item.festival_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Festival>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const columns: TableColumnsType<Festival> = [
    {
      title: 'Festival Name',
      dataIndex: 'festival_name',
      key: 'festival_name',
      filters: Array.from(new Set(festivals.map((f) => f.festival_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.festival_name || null,
      onFilter: (value, record) => record.festival_name.includes(value as string),
      sorter: (a, b) => a.festival_name.localeCompare(b.festival_name),
      sortOrder: sortedInfo.columnKey === 'festival_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.festival_name}</div>,
    },
  ];

  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Festival) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.festival_status}
                onChange={() => handleStatusToggle(record.festival_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this festival?"
              onConfirm={() => handleDelete(record.festival_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb
            style={{ display: 'inline-block', margin: 0 }}
            items={[
              {
                href: '/',
                title: <HomeOutlined />,
              },
              {
                title: 'View Festivals',
              },
            ]}
          />

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Festival
            </Button>
          </Flex>
        )}
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Festival>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="festival_id"
          dataSource={festivals.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No festival found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, festivals.length)} of{' '}
                {festivals.length}
              </span>
              <Pagination
                {...pagination}
                total={festivals.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <FestivalModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default Festivals;
